import { DesktopProduit, DesktopVersion } from '../types';

export const DESKTOP_PRODUITS: { code: DesktopProduit; nom: string }[] = [
  { code: 'SCF', nom: 'WINALCO SCF' },
  { code: 'GSCOM', nom: 'WINALCO GSCOM' },
  { code: 'PAIE', nom: 'WINALCO Paie' },
  // Le service de fichiers et les fichiers qu'il sert sous la racine de chaque zone
  // (mise a jour automatique du service, 25/09/2026)
  { code: 'SVC', nom: 'Service de fichiers' },
  { code: 'RSCF', nom: 'Racine SCF (etats, modeles, scripts)' },
  { code: 'RGSCOM', nom: 'Racine GSCOM (etats, modeles, scripts)' },
  { code: 'RPAIE', nom: 'Racine Paie (etats, modeles, scripts)' },
  // Smart Check, variante Firebird (10/10/2026)
  { code: 'CHEQUE', nom: 'Smart Check (Firebird)' },
];

export const nomProduit = (code: string): string =>
  DESKTOP_PRODUITS.find((produit) => produit.code === code)?.nom ?? code;

const VERSION_MAX = 65535;

/**
 * Quatre entiers de 0 à 65535 séparés par des points : la règle du serveur et des postes
 * (forme de la ressource de version Windows). Renvoie les entiers, ou null si la forme est
 * refusée. Aucun espace toléré : le poste envoie sa version telle quelle.
 */
export const parseVersion = (texte: string): number[] | null => {
  if (!/^[0-9]{1,5}\.[0-9]{1,5}\.[0-9]{1,5}\.[0-9]{1,5}$/.test(texte)) {
    return null;
  }
  const parts = texte.split('.').map(Number);
  return parts.every((part) => part <= VERSION_MAX) ? parts : null;
};

/** Comparaison entier par entier, jamais en texte : 1.6.0.10 > 1.6.0.9. */
export const compareVersions = (a: number[], b: number[]): number => {
  for (let index = 0; index < 4; index += 1) {
    if (a[index] !== b[index]) {
      return a[index] - b[index];
    }
  }
  return 0;
};

/**
 * Version déjà inscrite pour ce produit sous la même valeur numérique (1.06.0.1 = 1.6.0.1),
 * en ignorant la ligne en cours de correction. Le serveur refuse ce cas ; la page le signale
 * dès la saisie.
 */
export const findSameNumber = (
  items: DesktopVersion[],
  produit: string,
  parts: number[],
  exceptId?: number
): DesktopVersion | undefined =>
  items.find((item) => {
    if (item.produit !== produit || item.id === exceptId) {
      return false;
    }
    const other = parseVersion(item.version);
    return other !== null && compareVersions(other, parts) === 0;
  });

/** Plus grande version publiée du produit, en excluant éventuellement une ligne. */
export const latestPublished = (
  items: DesktopVersion[],
  produit: string,
  exceptId?: number
): DesktopVersion | undefined => {
  let best: DesktopVersion | undefined;
  let bestParts: number[] | null = null;
  items.forEach((item) => {
    if (!item.publiee || item.produit !== produit || item.id === exceptId) {
      return;
    }
    const parts = parseVersion(item.version);
    if (parts && (!bestParts || compareVersions(parts, bestParts) > 0 ||
        (compareVersions(parts, bestParts) === 0 && best && item.id > best.id))) {
      best = item;
      bestParts = parts;
    }
  });
  return best;
};

/** Taille lisible : 81 234 567 octets -> « 77,5 Mo ». */
export const formatTaille = (octets: number | null | undefined): string => {
  if (octets === null || octets === undefined) {
    return '-';
  }
  if (octets < 1024 * 1024) {
    return `${Math.max(1, Math.round(octets / 1024)).toLocaleString('fr-FR')} Ko`;
  }
  return `${(octets / (1024 * 1024)).toLocaleString('fr-FR', { maximumFractionDigits: 1 })} Mo`;
};

/**
 * Ce qu'il faut dire du lot d'une version (palier 2).
 *  - `aucun`   : pas de lot rattaché — les postes ne téléchargent rien, c'est le palier 1 ;
 *  - `present` : le fichier est dans le dépôt, de la taille enregistrée ;
 *  - `absent`  : un lot est enregistré mais son fichier manque du dépôt (ou n'a plus la même
 *                taille). Les postes ne reçoivent alors AUCUNE adresse, et publier est refusé.
 */
export const resumeLot = (item: {
  lot_fichier: string | null;
  lot_taille_octets: number | null;
  lot_fichier_ok: boolean | null;
}): { etat: 'aucun' | 'present' | 'absent'; texte: string } => {
  if (!item.lot_fichier) {
    return { etat: 'aucun', texte: 'Aucun lot' };
  }
  if (item.lot_fichier_ok === false) {
    return { etat: 'absent', texte: 'Fichier absent du dépôt' };
  }
  return { etat: 'present', texte: formatTaille(item.lot_taille_octets) };
};

/** AAAA-MM-JJ -> JJ/MM/AAAA. */
export const formatDateCourte = (iso: string | null | undefined): string => {
  if (!iso) {
    return '-';
  }
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  return match ? `${match[3]}/${match[2]}/${match[1]}` : iso;
};

/** Saisie de la fenêtre Inscrire / Corriger (champs texte tels que saisis). */
export interface VersionSaisie {
  produit: DesktopProduit;
  version: string;
  publiee_le: string;
  taille_octets: string;
  obligatoire: boolean;
  notes: string;
}

/**
 * Limite des notes, en caractères tels que JavaScript et le serveur les comptent tous deux
 * (unités UTF-16 : la plupart des émojis en valent deux), quels qu'ils soient. Le serveur compte
 * après avoir ramené les fins de ligne à LF et retiré les blancs des extrémités : il n'en
 * compte donc jamais plus que la page.
 */
export const NOTES_MAX = 4000;

/** Refus de notes trop longues, tel que la fenêtre l'affiche ; chaîne vide si elles passent. */
export const erreurNotes = (notes: string): string =>
  notes.length > NOTES_MAX ? `${NOTES_MAX} caractères au plus` : '';

/** Notes comme le serveur les enregistre : fins de ligne LF, blancs des extrémités retirés. */
export const normaliserNotes = (notes: string): string => notes.replace(/\r\n?/g, '\n').trim();

/** Échec d'un appel : le message du serveur et, pour une panne (500), sa cause (`detail`). */
export interface ErreurAppel {
  message: string;
  detail: string;
}

export const lireErreur = (err: any, defaut: string): ErreurAppel => {
  const data = err?.response?.data;
  const texte = (valeur: unknown): string => (typeof valeur === 'string' ? valeur.trim() : '');
  return {
    message: texte(data?.message) || texte(data?.error) || texte(err?.message) || defaut,
    detail: texte(data?.detail),
  };
};

/** Le même échec en une phrase : « Erreur interne : <cause> », ou le message seul. */
export const messageErreur = (err: any, defaut: string): string => {
  const { message, detail } = lireErreur(err, defaut);
  return detail ? `${message} : ${detail}` : message;
};

/**
 * Changements entre deux saisies, en clair (« Publiée le : 16/12/2026 → 16/09/2026 »). Les notes
 * ne sont pas recopiées. Liste vide : la correction ne change rien.
 */
export const changementsSaisie = (avant: VersionSaisie, apres: VersionSaisie): string[] => {
  const changements: string[] = [];
  const date = (iso: string) => (iso === '' ? 'aucune' : formatDateCourte(iso));
  const taille = (texte: string, autre: string) => {
    if (texte.trim() === '') {
      return 'aucune';
    }
    const lisible = formatTaille(Number(texte.trim()));
    return autre.trim() !== '' && lisible === formatTaille(Number(autre.trim()))
      ? `${Number(texte.trim()).toLocaleString('fr-FR')} octets`
      : lisible;
  };
  if (avant.produit !== apres.produit) {
    changements.push(`Produit : ${nomProduit(avant.produit)} → ${nomProduit(apres.produit)}`);
  }
  if (avant.version.trim() !== apres.version.trim()) {
    changements.push(`Version : ${avant.version.trim()} → ${apres.version.trim()}`);
  }
  if (avant.publiee_le !== apres.publiee_le) {
    changements.push(`Publiée le : ${date(avant.publiee_le)} → ${date(apres.publiee_le)}`);
  }
  if (avant.taille_octets.trim() !== apres.taille_octets.trim()) {
    changements.push(`Taille : ${taille(avant.taille_octets, apres.taille_octets)} → ` +
      `${taille(apres.taille_octets, avant.taille_octets)}`);
  }
  if (avant.obligatoire !== apres.obligatoire) {
    changements.push(`Obligatoire : ${avant.obligatoire ? 'oui' : 'non'} → ${apres.obligatoire ? 'oui' : 'non'}`);
  }
  if (normaliserNotes(avant.notes) !== normaliserNotes(apres.notes)) {
    changements.push('Notes modifiées');
  }
  return changements;
};

/**
 * Dernière version publiée du produit si la ligne `id`, publiée, prend ce produit et ce numéro :
 * ce que verront les postes du produit après la correction.
 */
export const derniereApresCorrection = (
  items: DesktopVersion[],
  id: number,
  produit: DesktopProduit,
  version: string
): DesktopVersion | undefined =>
  latestPublished(
    items.map((item) => (item.id === id ? { ...item, produit, version } : item)),
    produit
  );
